import { Typography } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import React, { useMemo } from 'react';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
  Tooltip,
} from 'recharts';
import {
  darkenColor,
  handleFocusRemoveSelectOutline,
} from '../Helper/pieChart.Helper';
import { type AssetData } from '../utils';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const renderActiveShape = (props: any) => {
  const RADIAN = Math.PI / 180;
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
    value,
  } = props;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = Number(cx) + (Number(outerRadius) + 10) * cos;
  const sy = Number(cy) + (Number(outerRadius) + 10) * sin;
  const mx = Number(cx) + (Number(outerRadius) + 30) * cos;
  const my = Number(cy) + (Number(outerRadius) + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';
  return (
    <g>
      <text
        x={cx}
        y={cy}
        dy={payload.name === 'total_part_cost' ? 14 : 0}
        textAnchor="middle"
        fill={fill}
      >
        {getName(payload.name)}
      </text>

      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={Number(outerRadius) + 6}
        outerRadius={Number(outerRadius) + 10}
        fill={fill}
      />
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        fill="none"
      />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text
        x={Number(ex) + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        dy={-12}
        textAnchor={textAnchor}
        fill={fill}
        fontSize={10}
      >{`Value:`}</text>
      <text
        x={Number(ex) + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        textAnchor={textAnchor}
        fill={fill}
        fontSize={14}
      >{`${value.toFixed(0)}`}</text>
      <text
        x={Number(ex) + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        dy={15}
        textAnchor={textAnchor}
        fill="#999"
        fontSize={10}
      >
        {`Rate`}
      </text>
      <text
        x={Number(ex) + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        dy={27}
        textAnchor={textAnchor}
        fill="#999"
        fontSize={14}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    </g>
  );
};

const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: any;
  label?: string;
  enableDarkTheme: boolean;
}> = ({ active, payload, label, enableDarkTheme = false }) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  if (active && payload && payload.length) {
    const hoverCell = payload[0].payload;

    return (
      <div className="custom-tooltip">
        <Typography
          variant="body2"
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('asset_name_2')} {getName(hoverCell.name)}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('total_asset_cost')} {hoverCell.value}
        </Typography>
      </div>
    );
  }
  return null;
};

export const ComponentsPieChart = ({
  data,
  handlePieClick,
  selectedAssetsCostType,
  enableDarkTheme = false,
  keysToAggregate = ['total_labour_cost', 'total_part_cost'],
}: {
  data: AssetData[];
  handlePieClick: any;
  selectedAssetsCostType: string[];
  enableDarkTheme: boolean;
  keysToAggregate?: string[];
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const onPieClick = (data: any, index: number) => {
    handlePieClick(data);
  };

  const aggregatedData = useMemo(() => {
    const aggregated =
      keysToAggregate?.map((key) => {
        return {
          name: key,
          value: data.reduce((sum, current) => {
            const currentValue = +current[key];
            return sum + currentValue;
          }, 0),
        };
      }) ?? [];

    return aggregated;
  }, [data, keysToAggregate]);

  const activeIndex = useMemo(() => {
    return aggregatedData
      .map((entry, index) =>
        selectedAssetsCostType.includes(entry.name) ? index : -1
      )
      .filter((i) => i !== -1);
  }, [aggregatedData, selectedAssetsCostType]);

  return (
    <ResponsiveContainer width="fit-content" height="100%">
      <div tabIndex={0} onFocus={handleFocusRemoveSelectOutline}>
        <PieChart width={500} height={400}>
          <Pie
            dataKey="value"
            isAnimationActive={true}
            data={aggregatedData}
            cx="50%"
            cy="50%"
            innerRadius={80}
            outerRadius={120}
            fill="#8884d8"
            onClick={onPieClick}
            activeIndex={activeIndex} // Pass active indices
            activeShape={renderActiveShape}
          >
            {aggregatedData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={
                  selectedAssetsCostType.includes(entry.name)
                    ? darkenColor(getColourByIndex(index))
                    : getColourByIndex(index)
                }
              />
            ))}
          </Pie>
          <Legend />
          <Tooltip
            content={<CustomTooltip enableDarkTheme={enableDarkTheme} />}
          />
        </PieChart>
      </div>
    </ResponsiveContainer>
  );
};

function getColourByIndex(index: any) {
  return index === 0 ? '#00C49F' : '#FFBB28';
}

function getName(name: string) {
  return name === 'total_part_cost' ? 'Part Cost' : 'Labour Cost';
}

export default ComponentsPieChart;
